import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, FormLabel, Option, Select, Typography } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import * as Yup from 'yup';

import '../EmailInvitesModal.css';
import Role from '../models/Role';
import { InvitationService } from '../services/InvitationService';
import { RoleService } from '../services/RoleService';
import organizationStore from '../stores/OrganizationStore';
import ChipsInputField from './FormElements/ChipsInputField';

export interface FormValues {
  emailInput?: string;
  role?: string;
}

interface EmailInvitesModalProps {
  onClose: () => void;
  onInviteSuccess: () => void;
}

const EmailInvitesModal: React.FC<EmailInvitesModalProps> = ({ onClose, onInviteSuccess }) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isFetchingRoles, setIsFetchingRoles] = useState<boolean>(true);
  const { selectedOrg } = organizationStore;
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    emailInput: Yup.string().email(t('invite_modal.errors.invalid_email')),
    role: Yup.string()
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      emailInput: '',
      role: ''
    },
    resolver: yupResolver(schema),
    mode: 'onChange'
  });
  const selectedRole = watch('role');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setRoles(await RoleService.getRoles());
        setIsFetchingRoles(false);
      } catch {
        toast.error(t('invite_modal.errors.fetch_data'));
      }
    };

    fetchData();
  }, [t]);

  const onSubmit = async (data: FormValues) => {
    if (selectedOrg === null) {
      toast.error(t('invite_modal.errors.org_selection'));
      return;
    }

    try {
      const roleId = Number(data.role);
      await InvitationService.sendMultipleOrgInvitations(emails, 'organization', selectedOrg, roleId);
      toast.success(t('invite_modal.invite_sent'));
      onClose();
      onInviteSuccess();
    } catch {
      toast.error(t('invite_modal.errors.sending_invite'));
      onClose();
    }
  };

  return (
    <form className='email-invites-modal' onSubmit={handleSubmit(onSubmit)}>
      {errors.emailInput && (
        <Typography color='danger' sx={{ marginBottom: 1 }}>
          {errors.emailInput.message}
        </Typography>
      )}
      <FormControl>
        <Controller
          control={control}
          name='emailInput'
          render={({ field }) => (
            <ChipsInputField<FormValues>
              chipDeleteSx={{ color: '#000000' }}
              chipSx={{ margin: 0.5, size: 'lg' }}
              clearInput={() => setValue('emailInput', '')}
              containerSx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'md'
              }}
              field={field}
              items={emails}
              placeholder={t('invite_modal.invite_others')}
              setItems={setEmails}
              triggerValidation={(name) => trigger(name as 'emailInput')}
            />
          )}
        />
      </FormControl>
      <div className='actions-wrapper'>
        <div className='role-wrapper'>
          <FormLabel htmlFor='role-select'>{t('invite_modal.add_as')}</FormLabel>
          <div className='role-select-wrapper'>
            <Controller
              control={control}
              name='role'
              render={({ field }) => (
                <Select
                  {...field}
                  disabled={isFetchingRoles}
                  id='role-select'
                  onChange={(event, newValue) => setValue('role', newValue ?? '')}
                  slotProps={{
                    listbox: {
                      sx: {
                        width: '100%'
                      }
                    }
                  }}
                  sx={{ minWidth: '12rem' }}
                  value={field.value || ''}
                >
                  {roles.map((role, index) => (
                    <Option key={index} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              )}
            />
            <Button
              color='neutral'
              disabled={emails.length === 0 || !selectedRole}
              sx={{ paddingY: '0.75rem' }}
              type='submit'
              variant='solid'
            >
              {t('invite_modal.send_invites')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default observer(EmailInvitesModal);
